import { type ReactNode, useCallback, useId } from 'react'
import { Button, RawButton } from '@/components/Button'
import { FileButton } from '@/components/FileButton'
import { Form, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from '@/components/ModalSheet'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import { pluralize } from '@/utils/pluralization-util'
import styles from './InvitationDialog.module.css'
import { type ParsedCustomerRow } from '@/features/iam/customers/customer-csv'

export type SignInType = 'UsernamePassword' | 'AccessLink'

/**
 * The outcome of inviting one row of the upload.
 */
export interface BulkUploadResult {
  email: string
  givenName: string
  familyName: string
  errorMessage?: string
}

export interface BulkUploadFormProps {
  fileName: string
  error: string
  customers: ParsedCustomerRow[]
  validCustomerCount: number
  isDisabled: boolean
  onFileSelected: (file: File) => void
  onRemoveCustomer: (index: number) => void
  onDownloadTemplate: () => void
  /**
   * Prefix for the `data-test` ids.
   */
  testId?: string
}

export function BulkUploadForm({
  fileName,
  error,
  customers,
  validCustomerCount,
  isDisabled,
  onFileSelected,
  onRemoveCustomer,
  onDownloadTemplate,
  testId,
}: BulkUploadFormProps) {
  const handleFileChange = useCallback(
    (files: FileList) => {
      if (files.length > 0) {
        onFileSelected(files[0])
      }
    },
    [onFileSelected],
  )

  return (
    <>
      <FormRow>
        <FormGroupContainer label="Upload CSV file">
          <div className={styles.uploadRow}>
            <FileButton
              accept=".csv"
              onChange={handleFileChange}
              isDisabled={isDisabled}
            >
              <Button
                label="Choose file"
                variant="secondary"
                isDisabled={isDisabled}
                testId={testId && `${testId}-bulk-upload-button`}
              />
            </FileButton>
            <span className={styles.fileName}>
              {fileName || 'No file selected'}
            </span>
          </div>
          <RawButton
            onPress={onDownloadTemplate}
            className={styles.downloadLink}
          >
            Download sample template
          </RawButton>
        </FormGroupContainer>
      </FormRow>

      {error && (
        <FormRow>
          <div className={styles.errorMessage}>{error}</div>
        </FormRow>
      )}

      {customers.length > 0 && (
        <FormRow>
          <FormGroupContainer
            label={`Preview (${validCustomerCount} valid of ${customers.length})`}
          >
            <div className={styles.previewTable}>
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer, index) => (
                    <tr
                      key={customer.rowNumber}
                      className={customer.isValid ? '' : styles.invalidRow}
                    >
                      <td>{customer.email || '-'}</td>
                      <td>{customer.givenName || '-'}</td>
                      <td>{customer.familyName || '-'}</td>
                      <td>{customer.phoneNumber || '-'}</td>
                      <td>
                        {customer.isValid ? (
                          <span className={styles.validStatus}>Valid</span>
                        ) : (
                          <span
                            className={styles.invalidStatus}
                            title={customer.errors.join(', ')}
                          >
                            {customer.errors[0]}
                          </span>
                        )}
                      </td>
                      <td>
                        <RawButton
                          onPress={() => onRemoveCustomer(index)}
                          className={styles.removeButton}
                          isDisabled={isDisabled}
                        >
                          Remove
                        </RawButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FormGroupContainer>
        </FormRow>
      )}
    </>
  )
}

interface BulkResultsViewProps {
  successfulResults: BulkUploadResult[]
  failedResults: BulkUploadResult[]
}

function BulkResultsView({
  successfulResults,
  failedResults,
}: BulkResultsViewProps) {
  const successCount = successfulResults.length
  const failedCount = failedResults.length

  return (
    <div className={styles.resultsView}>
      <div className={styles.resultsSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryCount}>{successCount}</span>
          <span className={styles.summaryLabel}>Created</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryCountFailed}>{failedCount}</span>
          <span className={styles.summaryLabel}>Failed</span>
        </div>
      </div>

      {successCount > 0 && (
        <div className={styles.resultsSection}>
          <h4 className={styles.resultsSectionTitle}>
            Successfully Created ({successCount})
          </h4>
          <div className={styles.resultsTable}>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {successfulResults.map((result) => (
                  <tr key={result.email} className={styles.successRow}>
                    <td>{result.email}</td>
                    <td>
                      {result.givenName} {result.familyName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {failedCount > 0 && (
        <div className={styles.resultsSection}>
          <h4 className={styles.resultsSectionTitle}>Failed ({failedCount})</h4>
          <div className={styles.resultsTable}>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {failedResults.map((result) => (
                  <tr key={result.email} className={styles.failedRow}>
                    <td>{result.email}</td>
                    <td>
                      {result.givenName} {result.familyName}
                    </td>
                    <td className={styles.errorCell}>{result.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export interface EntryModeSelectorProps {
  bulkUploadMode: boolean
  onBulkUploadModeChange: (bulkUploadMode: boolean) => void
  isDisabled: boolean
}

export function EntryModeSelector({
  bulkUploadMode,
  onBulkUploadModeChange,
  isDisabled,
}: EntryModeSelectorProps) {
  return (
    <FormRow>
      <RadioButtonGroup label="Entry Mode" size="big-wide">
        <RadioButtonItem
          active={!bulkUploadMode}
          onPress={() => onBulkUploadModeChange(false)}
          isDisabled={isDisabled}
        >
          Single
        </RadioButtonItem>
        <RadioButtonItem
          active={bulkUploadMode}
          onPress={() => onBulkUploadModeChange(true)}
          isDisabled={isDisabled}
        >
          Bulk Upload
        </RadioButtonItem>
      </RadioButtonGroup>
    </FormRow>
  )
}

export interface SignInTypeSelectorProps {
  signInType: SignInType
  onSignInTypeChange: (signInType: SignInType) => void
  isDisabled: boolean
}

export function SignInTypeSelector({
  signInType,
  onSignInTypeChange,
  isDisabled,
}: SignInTypeSelectorProps) {
  return (
    <FormRow>
      <RadioButtonGroup label="Sign-in Type" size="big-wide">
        <RadioButtonItem
          active={signInType === 'UsernamePassword'}
          onPress={() => onSignInTypeChange('UsernamePassword')}
          isDisabled={isDisabled}
        >
          Username & Password
        </RadioButtonItem>
        <RadioButtonItem
          active={signInType === 'AccessLink'}
          onPress={() => onSignInTypeChange('AccessLink')}
          isDisabled={isDisabled}
        >
          Access Link
        </RadioButtonItem>
      </RadioButtonGroup>
    </FormRow>
  )
}

export interface InvitationResultsDialogProps {
  showing: boolean
  onDone: () => void
  onRetryFailed: () => void
  successfulResults: BulkUploadResult[]
  failedResults: BulkUploadResult[]
  /**
   * Prefix for the `data-test` ids.
   */
  testId?: string
}

/**
 * Shown in place of the invitation form after a bulk submission.
 */
export function InvitationResultsDialog({
  showing,
  onDone,
  onRetryFailed,
  successfulResults,
  failedResults,
  testId,
}: InvitationResultsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onDone}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetSection horizontallySpaced>
          <ModalSheetContentTitle>Bulk Import Results</ModalSheetContentTitle>
          <ModalSheetContentDescription compact>
            {successfulResults.length > 0 && failedResults.length === 0
              ? 'All customers were created successfully.'
              : failedResults.length > 0 && successfulResults.length === 0
                ? 'All customer creations failed.'
                : 'Some customers were created, but others failed.'}
          </ModalSheetContentDescription>
        </ModalSheetSection>

        <ModalSheetSection spaced>
          <BulkResultsView
            successfulResults={successfulResults}
            failedResults={failedResults}
          />
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {failedResults.length > 0 && (
            <Button
              label={`Retry Failed (${failedResults.length})`}
              variant="secondary"
              onPress={onRetryFailed}
              testId={testId && `${testId}-retry-failed`}
            />
          )}
          <Button
            label="Done"
            variant="primary"
            onPress={onDone}
            testId={testId && `${testId}-done`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface InvitationDialogProps {
  title: string
  description: string
  showing: boolean
  onClose: () => void
  signInType: SignInType
  bulkUploadMode: boolean
  validBulkCustomerCount: number
  emailAddressCount: number
  onSubmit: () => void
  isSubmitPending: boolean
  /**
   * The form fields.
   */
  children: ReactNode
  /**
   * Prefix for the `data-test` ids.
   */
  testId?: string
}

export function InvitationDialog({
  title,
  description,
  showing,
  onClose,
  signInType,
  bulkUploadMode,
  validBulkCustomerCount,
  emailAddressCount,
  onSubmit,
  isSubmitPending,
  children,
  testId,
}: InvitationDialogProps) {
  const formId = useId()
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetSection horizontallySpaced>
          <ModalSheetContentTitle>{title}</ModalSheetContentTitle>
          <ModalSheetContentDescription compact>
            {description}
          </ModalSheetContentDescription>
        </ModalSheetSection>

        <ModalSheetSection spaced>
          <Form id={formId} onSubmit={onSubmit}>
            {children}
          </Form>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />
          <Button
            label={
              signInType === 'AccessLink'
                ? bulkUploadMode
                  ? `Create ${validBulkCustomerCount} ${pluralize('customer', validBulkCustomerCount)}`
                  : 'Create new customer'
                : `Send ${pluralize('invite', emailAddressCount)}`
            }
            type="submit"
            form={formId}
            variant="primary"
            isDisabled={
              isSubmitPending ||
              (signInType === 'AccessLink' &&
                bulkUploadMode &&
                validBulkCustomerCount === 0)
            }
            isPending={isSubmitPending}
            testId={testId && `${testId}-submit`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
